import {
  type Transaction,
  TransactionActivity,
  TransactionDocument,
  TransactionLineItem,
} from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  toast,
  useConfirm,
} from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useStore } from 'zustand';

import { useAuth } from '../../../context/AuthContext';
import { LineItemValidator } from '../../../context/TransactionLIneItems';
import { useTransactionLineItemsStore } from '../../../context/TransactionLIneItems/TransactionLInteItemsStoreContext';
import { withTransactionLineItemsStoreContext } from '../../../context/TransactionLIneItems/withTransactionLineItemsStoreContext';
import { queryKeys } from '../../../hooks/react-query/constants';
import { useTransactionDocumentsBatchQuery } from '../../../hooks/react-query/useTransactionDocumentQuery';
import { useTransactionLineItemBatchMutation } from '../../../hooks/react-query/useTransactionLineItem';
import { useTransactionBatchGetQuery } from '../../../hooks/react-query/useTransactionQuery';
import {
  S3ClientService,
  TransactionActivityClientService,
  TransactionDocumentClientService,
} from '../../../tools/helpers';
import { Loader } from '../../Loader/main';
import { TransactionLineItemsTable } from '../../TransactionUser/components/TransactionLineItems/TransactionLineItems';
import { TransactionTable } from '../TransactionTable';

interface Props {
  transactionId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReconciled?: () => void;
  defaultJobId?: number;
}

const validateNotes = (notes: string) => notes.trim().length > 0;

/**
 * Standalone Reconcile Accounts Payable dialog. Mirrors the reconcile flow on the
 * receipts page (TransactionUser card), but never submits the transaction — no
 * status, isBillingRecorded, or isAudited change. Matched line items get a
 * parentTransactionId, which removes them from the cost report.
 */
export const ReconcileAccountsPayableDialog = ({
  transactionId,
  open,
  onOpenChange,
  onReconciled,
  defaultJobId,
}: Props) => {
  const loggedUser = useAuth().user;
  const bypassCostCenterJobNumber = useMemo(
    () => loggedUser.permissionGroups.some((pg) => pg.name === 'AccountsPayableAdmin'),
    [loggedUser.permissionGroups],
  );

  const transactionQuery = useTransactionBatchGetQuery({
    filters: {
      id: transactionId,
      isActive: 1,
      includeTransactionLineItems: true,
    },
    enabled: open && transactionId !== 0,
    select(data) {
      return data.results.at(0);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="@container max-h-svh min-w-max max-w-screen-2xl p-0"
        showClose={false}
      >
        {transactionQuery.isPending && (
          <div className="flex min-h-40 items-center justify-center p-8">
            <Loader />
          </div>
        )}
        {transactionQuery.isError && (
          <div className="p-8">
            <p>Failed to load the transaction.</p>
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
        {transactionQuery.isSuccess && !transactionQuery.data && (
          <div className="p-8">
            <p>Transaction not found.</p>
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
        {transactionQuery.isSuccess && transactionQuery.data && (
          <ReconcileContent
            transaction={transactionQuery.data}
            circumventCostCenterRequired={bypassCostCenterJobNumber}
            onOpenChange={onOpenChange}
            onReconciled={onReconciled}
            defaultJobId={defaultJobId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface ContentProps {
  transaction: Transaction;
  circumventCostCenterRequired?: boolean;
  onOpenChange: (open: boolean) => void;
  onReconciled?: () => void;
  defaultJobId?: number;
}

const ReconcileContent = withTransactionLineItemsStoreContext(
  ({ transaction, onOpenChange, onReconciled, defaultJobId }: ContentProps) => {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const loggedUserId = useAuth().user.id;
    const lineItemsStore = useTransactionLineItemsStore();
    const transactionLineItemsBatchMutation = useTransactionLineItemBatchMutation();

    const [reconciledTransactions, setReconciledTransactions] = useState<Transaction[]>([]);
    const [isReconciling, setIsReconciling] = useState(false);

    const selectedReconcileTransactionAmountSum = useMemo(() => {
      return reconciledTransactions.reduce((sum, txn) => sum + txn.amount, 0);
    }, [reconciledTransactions]);

    const matchedAccountsPayable = useMemo(() => {
      const matched = transaction.transactionLineItems.filter((li) => !!li.parentTransactionId);
      return {
        ids: matched
          .map((li) => li.parentTransactionId)
          .filter((id): id is number => id !== undefined && id !== 0),
        amountSum: matched.reduce((sum, li) => sum + li.amount, 0),
      };
    }, [transaction.transactionLineItems]);
    const hasAssociatedAccountsPayable = matchedAccountsPayable.ids.length > 0;

    const [view, setView] = useState<'select' | 'finalize'>(
      hasAssociatedAccountsPayable ? 'finalize' : 'select',
    );

    // The backend auto-creates a full-amount mirror line item when a credit card
    // transaction is submitted (no parent, whole transaction amount). Once matches
    // exist it always pushes the line item total over the transaction amount, so
    // the reconcile flow treats it as replaceable everywhere: Set Match deletes it
    // outright and Finish Reconcile clears any that remain.
    const isDefaultMirrorLineItem = useCallback(
      (li: TransactionLineItem) =>
        !li.parentTransactionId && !li.secondaryAmount && li.amount === transaction.amount,
      [transaction.amount],
    );

    const storeLineItems = useStore(lineItemsStore, (s) => s.lineItems);
    const areLineItemsValid = useMemo(() => {
      const nonMirrorLineItems = storeLineItems.filter((li) => !isDefaultMirrorLineItem(li));
      return (
        nonMirrorLineItems.length > 0 &&
        LineItemValidator.isValidForSave({
          lineItems: nonMirrorLineItems,
          parentTransaction: transaction,
        })
      );
    }, [storeLineItems, isDefaultMirrorLineItem, transaction]);

    const documentCheckQuery = useTransactionDocumentsBatchQuery({
      filter: {
        transactionId: transaction.id,
      },
      select(data) {
        return data.totalCount;
      },
    });

    const finishReconcile = async (lineItems: TransactionLineItem[]) => {
      if (!documentCheckQuery.isSuccess)
        throw new Error('Document check query has not been finished on reconcile');

      if (!validateNotes(transaction.notes)) {
        toast({
          title: 'Error',
          description: 'Notes is required field',
          variant: 'destructive',
        });
        return;
      }

      const filesAmount: number = documentCheckQuery.data;

      if (filesAmount < 1) {
        toast({
          title: 'Error',
          description: 'Notes and documents are required fields',
          variant: 'destructive',
        });
        return;
      }

      setIsReconciling(true);
      try {
        const parentTransactionIds = lineItems
          .map((li) => li.parentTransactionId)
          .filter((id): id is number => id !== undefined && id !== 0);
        await Promise.all(
          parentTransactionIds.map((parentId) => {
            return new Promise(async (resolve, reject) => {
              try {
                const documentReq = TransactionDocument.create({
                  transactionId: parentId,
                });
                const existingDocuments =
                  await TransactionDocumentClientService.BatchGet(documentReq);
                if (existingDocuments.results) {
                  await Promise.all(
                    existingDocuments.results.map((existingDocument) => {
                      return new Promise(async (resolve, reject) => {
                        const oldDocument = {
                          bucket: 'kalos-transactions',
                          key: `${existingDocument.transactionId}-${existingDocument.reference}`,
                        };
                        const newDocument = {
                          bucket: 'kalos-transactions',
                          key: `${transaction.id}-${existingDocument.reference}`,
                        };
                        try {
                          await S3ClientService.moveFileBetweenS3Buckets(
                            oldDocument,
                            newDocument,
                            true,
                          );
                          const duplicate = TransactionDocument.create(existingDocument);
                          duplicate.transactionId = transaction.id;
                          await TransactionDocumentClientService.Create(duplicate);
                          resolve(null);
                        } catch {
                          reject();
                        }
                      });
                    }),
                  );
                }
                resolve(null);
              } catch (err) {
                reject(err);
              }
            });
          }),
        );
        TransactionActivityClientService.Create(
          TransactionActivity.create({
            transactionId: transaction.id,
            userId: loggedUserId,
            description: `Reconciled with transactions ${parentTransactionIds.join(', ')}`,
          }),
        );
        queryClient.invalidateQueries({
          queryKey: [queryKeys.transaction.root],
        });
        setReconciledTransactions([]);
        toast({
          title: 'Success',
          description: 'Transaction reconciled successfully',
          variant: 'success',
        });
        onReconciled?.();
        onOpenChange(false);
      } catch {
        toast({
          title: 'Error',
          description: 'An error occurred while reconciling the transaction',
          variant: 'destructive',
        });
      } finally {
        setIsReconciling(false);
      }
    };

    const confirmReconcile = async () => {
      const ok = await confirm(
        'Are you sure this is correct? We will match this transaction to the Accounts Payable Record',
      );
      if (!ok) return;
      setIsReconciling(true);
      try {
        const lineItems = reconciledTransactions.map((accountPayableTransaction) =>
          TransactionLineItem.create({
            transactionId: transaction.id,
            amount: accountPayableTransaction.amount,
            costCenterId: accountPayableTransaction.costCenterId,
            departmentId: accountPayableTransaction.departmentId,
            ownerId: transaction.ownerId,
            jobId: accountPayableTransaction.jobId,
            description: `Account Payable Match.
          Invoice number: ${accountPayableTransaction.invoiceNumber}
          Order number: ${accountPayableTransaction.orderNumber}
          `,
            parentTransactionId: accountPayableTransaction.id,
          }),
        );
        const existingLineItems = lineItemsStore.getState().lineItems;
        const defaultMirrorLineItems = existingLineItems.filter(isDefaultMirrorLineItem);
        const keptLineItems = existingLineItems.filter((li) => !isDefaultMirrorLineItem(li));
        const deletedLineItemIds = defaultMirrorLineItems.map((li) => li.id);

        const mutationResults = await transactionLineItemsBatchMutation.mutateAsync({
          lineItemsToCreate: lineItems,
          lineItemsToDelete: defaultMirrorLineItems,
          lineItemsToUpdate: [],
        });

        setReconciledTransactions([]);

        const newLineItems = mutationResults.filter((li) => !deletedLineItemIds.includes(li.id));
        const allLineItems = [...keptLineItems, ...newLineItems];
        const shouldContinue = LineItemValidator.isValidForSave({
          lineItems: allLineItems,
          parentTransaction: transaction,
        });

        if (!shouldContinue) {
          toast({
            title: 'Action needed',
            description:
              'Transaction Line items created. Please review and ensure all line items are valid',
          });
          setView('finalize');
          setIsReconciling(false);
          return;
        }

        await finishReconcile(allLineItems);
      } catch {
        toast({
          title: 'Error',
          description: 'An error occurred while reconciling the transaction',
          variant: 'destructive',
        });
      } finally {
        setIsReconciling(false);
      }
    };

    const finishFromReview = async () => {
      const currentLineItems = lineItemsStore.getState().lineItems;
      const mirrorLineItems = currentLineItems.filter(isDefaultMirrorLineItem);
      if (mirrorLineItems.length > 0) {
        setIsReconciling(true);
        try {
          await transactionLineItemsBatchMutation.mutateAsync({
            lineItemsToCreate: [],
            lineItemsToDelete: mirrorLineItems,
            lineItemsToUpdate: [],
          });
        } catch {
          toast({
            title: 'Error',
            description: 'An error occurred while reconciling the transaction',
            variant: 'destructive',
          });
          return;
        } finally {
          setIsReconciling(false);
        }
      }
      await finishReconcile(currentLineItems.filter((li) => !isDefaultMirrorLineItem(li)));
    };

    const handleClose = async () => {
      if (reconciledTransactions.length > 0) {
        if (!(await confirm('Are you sure you want to close this dialog without reconciling?')))
          return;
      }
      setReconciledTransactions([]);
      onOpenChange(false);
    };

    if (!transaction.artificialId) {
      return (
        <div className="p-8">
          <p>Only credit card transactions can be reconciled.</p>
          <div className="mt-4 flex justify-end">
            <Button size="sm" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </div>
      );
    }

    return (
      <>
        <DialogHeader className="@sm:p-4 flex flex-col items-center justify-between p-2 pb-0 md:flex-row">
          <div className="flex gap-4">
            <h1 className="mb-4 text-center text-base font-bold md:text-2xl">
              Reconcile Accounts Payable
            </h1>

            <div className="flex flex-col gap-1">
              <div>Receipt Total: ${transaction.amount.toFixed(2)}</div>
              {hasAssociatedAccountsPayable && (
                <div>Already matched: ${matchedAccountsPayable.amountSum.toFixed(2)}</div>
              )}
              {view === 'select' && (
                <div>Total selected: ${selectedReconcileTransactionAmountSum.toFixed(2)}</div>
              )}
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button size="sm" onClick={handleClose}>
              Close
            </Button>
            {view === 'select' ? (
              <>
                {hasAssociatedAccountsPayable && (
                  <Button size="sm" onClick={() => setView('finalize')}>
                    Review line items
                  </Button>
                )}
                <Sheet>
                  <SheetTrigger asChild>
                    <Button size="sm" disabled={reconciledTransactions.length === 0}>
                      Preview selected transactions
                    </Button>
                  </SheetTrigger>

                  <SheetContent
                    side="right"
                    className="@container max-h-svh w-screen overflow-auto md:w-3/4"
                  >
                    <SheetHeader>
                      <SheetTitle>Preview selected transactions</SheetTitle>
                    </SheetHeader>
                    <ul className="@md:gap-4 @lg:grid-cols-2 @3xl:grid-cols-3 grid grid-cols-1 gap-2 py-4 text-sm md:text-base">
                      {reconciledTransactions.map((txn) => {
                        const date = format(new Date(parseISO(txn.timestamp)), 'MM/dd/yyyy');
                        return (
                          <Card
                            aria-roledescription="list item"
                            key={txn.id}
                            className="grid grid-cols-[80px_1fr] p-2"
                          >
                            <div className="font-semibold">Order #</div>
                            <div className="break-all">{txn.orderNumber || 'N/A'}</div>
                            <div className="font-semibold">Invoice #</div>
                            <div className="break-all">{txn.invoiceNumber || 'N/A'}</div>
                            <div className="font-semibold">Date</div>
                            <div>{date}</div>
                            <div className="font-semibold">Amount</div>
                            <div>${txn.amount.toFixed(2)}</div>
                            <div className="font-semibold">Job #</div>
                            <div>{txn.jobId || 'N/A'}</div>
                            <div className="col-span-2 mt-2">
                              <Button
                                variant="destructive"
                                size="sm"
                                className="w-full"
                                onClick={() => {
                                  setReconciledTransactions((prev) =>
                                    prev.filter((t) => t.id !== txn.id),
                                  );
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          </Card>
                        );
                      })}
                    </ul>
                  </SheetContent>
                </Sheet>
                <Button
                  size="sm"
                  onClick={() => confirmReconcile()}
                  disabled={isReconciling || reconciledTransactions.length === 0}
                  isLoading={isReconciling}
                >
                  Set Match ({reconciledTransactions.length})
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" onClick={() => setView('select')}>
                  Add more matches
                </Button>
                <Button
                  size="sm"
                  onClick={finishFromReview}
                  disabled={
                    isReconciling || !areLineItemsValid || !validateNotes(transaction.notes)
                  }
                  isLoading={isReconciling}
                >
                  Finish Reconcile
                </Button>
              </>
            )}
          </div>
        </DialogHeader>
        <div className="h-[80vh] overflow-auto">
          {view === 'select' ? (
            <TransactionTable
              // PROCESSED
              defaultStatusId={5}
              defaultJobId={defaultJobId}
              disableFilterStatusSelector
              isSelector={true}
              selectionLimit={Infinity}
              disabledSelectionIds={matchedAccountsPayable.ids}
              selectorTitle="Select a transaction to reconcile"
              setTransactions={(data) => setReconciledTransactions(data)}
            />
          ) : (
            <div className="p-2">
              {!areLineItemsValid && (
                <p className="p-2 text-sm">
                  Please adjust line items to finalize reconciliation OR delete associated line
                  items. Line item amounts must add up to the transaction total ($
                  {transaction.amount.toFixed(2)}).
                </p>
              )}
              <TransactionLineItemsTable />
            </div>
          )}
        </div>
      </>
    );
  },
);
